import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { toast } from 'sonner';
import { 
  Award, 
  Plus, 
  Pencil, 
  Trash2, 
  Users,
  DollarSign,
  TrendingUp,
  Settings,
  Info,
  ChevronRight,
  Percent
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AffiliateTierManagement = () => {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTierModal, setShowTierModal] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tierToDelete, setTierToDelete] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    tier_name: '',
    display_name: '',
    description: '',
    color: '#808080',
    commission_rate: 0.20,
    min_conversions: 0,
    max_conversions: null,
    subscription_multipliers: {
      starter: 1.0,
      professional: 1.2,
      business: 1.5,
      enterprise: 2.0
    },
    benefits: [],
    features: []
  });
  
  const [newBenefit, setNewBenefit] = useState('');
  const [newFeature, setNewFeature] = useState('');

  useEffect(() => {
    fetchTiers();
  }, []);

  const fetchTiers = async () => {
    try {
      setLoading(true);
      const token = Cookies.get('enakid_token');
      const response = await axios.get(`${API}/api/admin/affiliate-tiers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTiers(response.data || []);
    } catch (error) {
      console.error('Error fetching tiers:', error);
      toast.error('Failed to load affiliate tiers');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateModal = () => {
    setEditingTier(null);
    setFormData({
      tier_name: '',
      display_name: '',
      description: '',
      color: '#808080',
      commission_rate: 0.20,
      min_conversions: 0,
      max_conversions: null,
      subscription_multipliers: {
        starter: 1.0,
        professional: 1.2,
        business: 1.5,
        enterprise: 2.0
      },
      benefits: [],
      features: []
    });
    setShowTierModal(true);
  };

  const handleOpenEditModal = (tier) => {
    setEditingTier(tier);
    setFormData({
      tier_name: tier.tier_name,
      display_name: tier.display_name,
      description: tier.description || '',
      color: tier.color || '#808080',
      commission_rate: tier.commission_rate,
      min_conversions: tier.min_conversions,
      max_conversions: tier.max_conversions,
      subscription_multipliers: tier.subscription_multipliers || {
        starter: 1.0,
        professional: 1.2,
        business: 1.5,
        enterprise: 2.0
      },
      benefits: tier.benefits || [],
      features: tier.features || []
    });
    setShowTierModal(true);
  };

  const handleSaveTier = async () => {
    try {
      const token = Cookies.get('enakid_token');
      
      // Validation
      if (!formData.tier_name || !formData.display_name) {
        toast.error('Tier name and display name are required');
        return;
      }
      
      if (formData.commission_rate < 0 || formData.commission_rate > 1) {
        toast.error('Commission rate must be between 0% and 100%');
        return;
      }
      
      if (formData.max_conversions && formData.max_conversions <= formData.min_conversions) {
        toast.error('Max conversions must be greater than min conversions');
        return;
      }

      if (editingTier) {
        // Update existing tier
        await axios.put(
          `${API}/api/admin/affiliate-tiers/${editingTier.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Tier updated successfully');
      } else {
        // Create new tier
        await axios.post(
          `${API}/api/admin/affiliate-tiers`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Tier created successfully');
      }

      setShowTierModal(false);
      fetchTiers();
    } catch (error) {
      console.error('Error saving tier:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to save tier';
      toast.error(errorMessage);
    }
  };

  const handleDeleteTier = async () => {
    try {
      const token = Cookies.get('enakid_token');
      await axios.delete(
        `${API}/api/admin/affiliate-tiers/${tierToDelete.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Tier deleted successfully');
      setDeleteDialogOpen(false);
      setTierToDelete(null);
      fetchTiers();
    } catch (error) {
      console.error('Error deleting tier:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to delete tier';
      toast.error(errorMessage);
    }
  };

  const handleAddBenefit = () => {
    if (newBenefit.trim()) {
      setFormData({
        ...formData,
        benefits: [...formData.benefits, newBenefit.trim()]
      });
      setNewBenefit('');
    }
  };

  const handleRemoveBenefit = (index) => {
    setFormData({
      ...formData,
      benefits: formData.benefits.filter((_, i) => i !== index)
    });
  };

  const handleAddFeature = () => {
    if (newFeature.trim()) {
      setFormData({
        ...formData,
        features: [...formData.features, newFeature.trim()]
      });
      setNewFeature('');
    }
  };

  const handleRemoveFeature = (index) => {
    setFormData({
      ...formData,
      features: formData.features.filter((_, i) => i !== index)
    });
  };

  const getConversionRangeText = (tier) => {
    if (tier.max_conversions === null) {
      return `${tier.min_conversions}+ conversions`;
    }
    return `${tier.min_conversions}-${tier.max_conversions} conversions`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Affiliate Tier Management</h1>
          <p className="text-gray-500 mt-1">Configure commission tiers and rates for your affiliate program</p>
        </div>
        <Button onClick={handleOpenCreateModal} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="h-4 w-4 mr-2" />
          Create Custom Tier
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Tiers</CardTitle>
            <Award className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{tiers.length}</div>
            <p className="text-xs text-muted-foreground">
              {tiers.filter(t => t.is_default).length} default, {tiers.filter(t => !t.is_default).length} custom
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Affiliates</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {tiers.reduce((sum, tier) => sum + (tier.affiliate_count || 0), 0)}
            </div>
            <p className="text-xs text-muted-foreground">Across all tiers</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Commission Rate</CardTitle>
            <Percent className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {tiers.length > 0
                ? `${((tiers.reduce((sum, tier) => sum + tier.commission_rate, 0) / tiers.length) * 100).toFixed(1)}%`
                : '0%'}
            </div>
            <p className="text-xs text-muted-foreground">Weighted average</p>
          </CardContent>
        </Card>
      </div>

      {/* Tiers Table */}
      <Card>
        <CardHeader>
          <CardTitle>Affiliate Tiers</CardTitle>
          <CardDescription>
            Manage commission tiers and their configurations. Default tiers can be modified but not deleted.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {tiers.map((tier) => (
              <div
                key={tier.id}
                className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center space-x-4 flex-1">
                  {/* Tier Badge */}
                  <div
                    className="w-16 h-16 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: tier.color + '20', border: `2px solid ${tier.color}` }}
                  >
                    <Award className="h-8 w-8" style={{ color: tier.color }} />
                  </div>

                  {/* Tier Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-semibold text-gray-900">{tier.display_name}</h3>
                      {tier.is_default && (
                        <Badge variant="outline" className="text-xs">
                          Default
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">{tier.description || tier.tier_name}</p>
                    <div className="flex items-center gap-4 mt-2">
                      <div className="flex items-center text-sm">
                        <DollarSign className="h-4 w-4 text-green-600 mr-1" />
                        <span className="font-medium">{(tier.commission_rate * 100).toFixed(0)}%</span>
                        <span className="text-gray-500 ml-1">base rate</span>
                      </div>
                      <div className="flex items-center text-sm">
                        <TrendingUp className="h-4 w-4 text-blue-600 mr-1" />
                        <span className="text-gray-600">{getConversionRangeText(tier)}</span>
                      </div>
                      <div className="flex items-center text-sm">
                        <Users className="h-4 w-4 text-purple-600 mr-1" />
                        <span className="font-medium">{tier.affiliate_count || 0}</span>
                        <span className="text-gray-500 ml-1">affiliates</span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenEditModal(tier)}
                    >
                      <Pencil className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setTierToDelete(tier);
                        setDeleteDialogOpen(true);
                      }}
                      disabled={tier.is_default}
                      className={tier.is_default ? 'opacity-50 cursor-not-allowed' : ''}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))}

            {tiers.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <Award className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No affiliate tiers configured yet.</p>
                <Button onClick={handleOpenCreateModal} className="mt-4">
                  Create First Tier
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit Tier Modal */}
      <Dialog open={showTierModal} onOpenChange={setShowTierModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTier ? 'Edit Affiliate Tier' : 'Create New Affiliate Tier'}
            </DialogTitle>
            <DialogDescription>
              Configure commission rates, thresholds, and benefits for this tier.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="tier_name">
                  Tier Name (Unique ID) <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="tier_name"
                  value={formData.tier_name}
                  onChange={(e) => setFormData({ ...formData, tier_name: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                  placeholder="e.g., diamond, vip, elite"
                  disabled={editingTier !== null}
                />
                <p className="text-xs text-gray-500">Lowercase, no spaces (use underscores)</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="display_name">
                  Display Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="display_name"
                  value={formData.display_name}
                  onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                  placeholder="e.g., Diamond, VIP, Elite"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this tier..."
                rows={2}
              />
            </div>

            {/* Color Picker */}
            <div className="space-y-2">
              <Label htmlFor="color">Tier Badge Color</Label>
              <div className="flex items-center gap-4">
                <Input
                  id="color"
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="w-24 h-10"
                />
                <div
                  className="w-12 h-12 rounded-lg border-2 flex items-center justify-center"
                  style={{ backgroundColor: formData.color + '20', borderColor: formData.color }}
                >
                  <Award className="h-6 w-6" style={{ color: formData.color }} />
                </div>
                <span className="text-sm text-gray-600">{formData.color}</span>
              </div>
            </div>

            {/* Commission Rate */}
            <div className="space-y-2">
              <Label htmlFor="commission_rate">
                Base Commission Rate <span className="text-red-500">*</span>
              </Label>
              <div className="flex items-center gap-4">
                <Input
                  id="commission_rate"
                  type="number"
                  step="0.01"
                  min="0"
                  max="1"
                  value={formData.commission_rate}
                  onChange={(e) => setFormData({ ...formData, commission_rate: parseFloat(e.target.value) })}
                  className="w-32"
                />
                <div className="flex-1">
                  <div className="w-full bg-gray-200 rounded-full h-4">
                    <div
                      className="bg-green-600 h-4 rounded-full transition-all"
                      style={{ width: `${formData.commission_rate * 100}%` }}
                    ></div>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    {(formData.commission_rate * 100).toFixed(0)}% commission rate
                  </p>
                </div>
              </div>
            </div>

            {/* Conversion Thresholds */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="min_conversions">
                  Min Conversions <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="min_conversions"
                  type="number"
                  min="0"
                  value={formData.min_conversions}
                  onChange={(e) => setFormData({ ...formData, min_conversions: parseInt(e.target.value) || 0 })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="max_conversions">Max Conversions</Label>
                <Input
                  id="max_conversions"
                  type="number"
                  min="0"
                  value={formData.max_conversions || ''}
                  onChange={(e) => setFormData({ ...formData, max_conversions: e.target.value ? parseInt(e.target.value) : null })}
                  placeholder="Leave empty for unlimited"
                />
                <p className="text-xs text-gray-500">Leave empty for highest tier</p>
              </div>
            </div>

            {/* Subscription Multipliers */}
            <div className="space-y-2">
              <Label>Subscription Tier Multipliers</Label>
              <div className="grid grid-cols-2 gap-3">
                {Object.keys(formData.subscription_multipliers).map((subTier) => (
                  <div key={subTier} className="flex items-center gap-2">
                    <Label className="w-24 capitalize text-sm">{subTier}:</Label>
                    <Input
                      type="number"
                      step="0.1"
                      min="1"
                      max="3"
                      value={formData.subscription_multipliers[subTier]}
                      onChange={(e) => setFormData({
                        ...formData,
                        subscription_multipliers: {
                          ...formData.subscription_multipliers,
                          [subTier]: parseFloat(e.target.value) || 1.0
                        }
                      })}
                      className="flex-1"
                    />
                    <span className="text-sm text-gray-500">×</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500">
                Multiplier applied to base commission for each subscription tier
              </p>
            </div>

            {/* Benefits */}
            <div className="space-y-2">
              <Label>Tier Benefits</Label>
              <div className="flex gap-2">
                <Input
                  value={newBenefit}
                  onChange={(e) => setNewBenefit(e.target.value)}
                  placeholder="Add a benefit..."
                  onKeyPress={(e) => e.key === 'Enter' && handleAddBenefit()}
                />
                <Button type="button" onClick={handleAddBenefit} variant="outline">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="space-y-2 mt-2">
                {formData.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-2 bg-gray-50 p-2 rounded">
                    <ChevronRight className="h-4 w-4 text-blue-600" />
                    <span className="flex-1 text-sm">{benefit}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveBenefit(index)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Features */}
            <div className="space-y-2">
              <Label>Technical Features (Optional)</Label>
              <div className="flex gap-2">
                <Input
                  value={newFeature}
                  onChange={(e) => setNewFeature(e.target.value)}
                  placeholder="e.g., priority_support, advanced_analytics"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddFeature()}
                />
                <Button type="button" onClick={handleAddFeature} variant="outline">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.features.map((feature, index) => (
                  <Badge key={index} variant="secondary" className="gap-2">
                    {feature}
                    <button onClick={() => handleRemoveFeature(index)}>×</button>
                  </Badge>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTierModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveTier} className="bg-blue-600 hover:bg-blue-700">
              {editingTier ? 'Update Tier' : 'Create Tier'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Affiliate Tier</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the <strong>{tierToDelete?.display_name}</strong> tier?
              {tierToDelete?.affiliate_count > 0 && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-red-800">
                  <Info className="h-4 w-4 inline mr-2" />
                  This tier has {tierToDelete.affiliate_count} affiliate(s) assigned. 
                  You must reassign them before deletion.
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTier}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete Tier
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AffiliateTierManagement;
